/***
*
*   PLAN EDITOR
*   Modal for creating/editing plans with features and add-ons
*
**********/

import { useState, useEffect } from 'react';
import { Button, Icon } from 'components/lib';
import { FeatureEditor } from './FeatureEditor';
import { AddOnsEditor } from './AddOnsEditor';
import Style from './bundlePackaging.tailwind.js';

export function PlanEditor({ plan, isNew, availableFeatures, availableAddOns, onSave, onCancel, loading }) {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    price: 0,
    discounted_price: null,
    discount_percent: null,
    interval: 'month',
    employee_limit: null,
    user_limit: null,
    trial_days: null,
    type: 'visible',
    currency: { name: 'usd', symbol: '$' },
    features: {},
    modules_enabled: [],
    add_ons_available: [],
  });

  const [activeSection, setActiveSection] = useState('basic');

  useEffect(() => {
    if (plan) {
      setFormData({
        ...plan,
        features: plan.features || {},
        modules_enabled: plan.modules_enabled || [],
        add_ons_available: plan.add_ons_available || [],
      });
    } else {
      // Reset form for new plan
      setFormData({
        id: '',
        name: '',
        price: 0,
        discounted_price: null,
        discount_percent: null,
        interval: 'month',
        employee_limit: null,
        user_limit: null,
        trial_days: null,
        type: 'visible',
        currency: { name: 'usd', symbol: '$' },
        features: {},
        modules_enabled: [],
        add_ons_available: [],
      });
    }
  }, [plan]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFeatureChange = (features) => {
    setFormData(prev => ({
      ...prev,
      features,
      modules_enabled: Object.keys(features)
    }));
  };

  const handleAddOnsChange = (addOns, updatedFeatures) => {
    setFormData(prev => ({
      ...prev,
      add_ons_available: addOns,
      features: updatedFeatures || prev.features,
      modules_enabled: updatedFeatures ? Object.keys(updatedFeatures) : prev.modules_enabled
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const sections = [
    { id: 'basic', label: 'Basic Info', icon: 'info' },
    { id: 'pricing', label: 'Pricing', icon: 'dollar-sign' },
    { id: 'features', label: 'Features', icon: 'list' },
    { id: 'addons', label: 'Add-ons', icon: 'package' },
    { id: 'limits', label: 'Limits', icon: 'sliders' },
  ];

  return (
    <div className={Style.modalOverlay} onClick={onCancel}>
      <div className={Style.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={Style.modalHeader}>
          <h2 className={Style.modalTitle}>
            {isNew ? 'Create New Plan' : `Edit ${plan?.name}`}
          </h2>
          <Button
            icon="x"
            color="gray"
            size="small"
            action={onCancel}
          />
        </div>

        {/* Section Tabs */}
        <div className="flex border-b border-slate-200 px-6 overflow-x-auto">
          {sections.map((section) => (
            <button
              key={section.id}
              type="button"
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeSection === section.id
                  ? 'text-brand-500 border-brand-500'
                  : 'text-slate-500 border-transparent hover:text-slate-700'
              }`}
              onClick={() => setActiveSection(section.id)}
            >
              <Icon image={section.icon} size={16} />
              {section.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className={Style.modalBody}>
            {/* Basic Info Section */}
            {activeSection === 'basic' && (
              <div className="space-y-4">
                <div className={Style.formGroup}>
                  <label className={Style.formLabel}>Plan Name *</label>
                  <input
                    type="text"
                    className={Style.formInput}
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="e.g., Professional Plan"
                    required
                  />
                </div>

                <div className={Style.formGroup}>
                  <label className={Style.formLabel}>Plan Type</label>
                  <select
                    className={Style.formSelect}
                    value={formData.type || 'visible'}
                    onChange={(e) => handleChange('type', e.target.value)}
                  >
                    <option value="visible">Visible</option>
                    <option value="hidden">Hidden</option>
                  </select>
                </div>

                <div className={Style.formGroup}>
                  <label className={Style.formLabel}>Billing Interval</label>
                  <select
                    className={Style.formSelect}
                    value={formData.interval}
                    onChange={(e) => handleChange('interval', e.target.value)}
                  >
                    <option value="month">Monthly</option>
                    <option value="year">Yearly</option>
                  </select>
                </div>
              </div>
            )}

            {/* Pricing Section */}
            {activeSection === 'pricing' && (
              <div className="space-y-4">
                <div className={Style.formGroup}>
                  <label className={Style.formLabel}>Price (per {formData.interval})</label>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-medium text-slate-600">$</span>
                    <input
                      type="number"
                      className={Style.formInput}
                      value={formData.price ?? ''}
                      onChange={(e) => handleChange('price', e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Leave empty for custom/quotation pricing</p>
                </div>

                <div className={Style.formGroup}>
                  <label className={Style.formLabel}>Discounted Price (optional)</label>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-medium text-slate-600">$</span>
                    <input
                      type="number"
                      className={Style.formInput}
                      value={formData.discounted_price ?? ''}
                      onChange={(e) => handleChange('discounted_price', e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>

                <div className={Style.formGroup}>
                  <label className={Style.formLabel}>Discount Percent (%)</label>
                  <input
                    type="number"
                    className={Style.formInput}
                    value={formData.discount_percent ?? ''}
                    onChange={(e) => handleChange('discount_percent', e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="0"
                    min="0"
                    max="100"
                  />
                </div>

                <div className={Style.formGroup}>
                  <label className={Style.formLabel}>Trial Days</label>
                  <input
                    type="number"
                    className={Style.formInput}
                    value={formData.trial_days ?? ''}
                    onChange={(e) => handleChange('trial_days', e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="14"
                    min="0"
                  />
                </div>
              </div>
            )}

            {/* Features Section */}
            {activeSection === 'features' && (
              <FeatureEditor
                features={formData.features}
                availableFeatures={availableFeatures}
                onChange={handleFeatureChange}
              />
            )}

            {/* Add-ons Section */}
            {activeSection === 'addons' && (
              <AddOnsEditor
                selectedAddOns={formData.add_ons_available}
                availableAddOns={availableAddOns}
                planFeatures={formData.features}
                onChange={handleAddOnsChange}
              />
            )}

            {/* Limits Section */}
            {activeSection === 'limits' && (
              <div className="space-y-4">
                <div className={Style.formGroup}>
                  <label className={Style.formLabel}>Employee Limit</label>
                  <input
                    type="number"
                    className={Style.formInput}
                    value={formData.employee_limit ?? ''}
                    onChange={(e) => handleChange('employee_limit', e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="Unlimited"
                    min="0"
                  />
                  <p className="text-xs text-slate-500 mt-1">Leave empty for unlimited</p>
                </div>

                <div className={Style.formGroup}>
                  <label className={Style.formLabel}>User Limit</label>
                  <input
                    type="number"
                    className={Style.formInput}
                    value={formData.user_limit ?? ''}
                    onChange={(e) => handleChange('user_limit', e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="Unlimited"
                    min="0"
                  />
                  <p className="text-xs text-slate-500 mt-1">Leave empty for unlimited</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={Style.modalFooter}>
            <Button
              text="Cancel"
              color="gray"
              outline
              action={onCancel}
            />
            <Button
              text={isNew ? 'Create Plan' : 'Save Changes'}
              color="green"
              loading={loading}
              action={handleSubmit}
            />
          </div>
        </form>
      </div>
    </div>
  );
}